import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authAPI } from "../lib/api";
import { toast } from "sonner";

const AuthContext = createContext(null);

const getTokenExpiry = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null; // convert to ms
  } catch {
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const savedUser = sessionStorage.getItem("user");

    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      // Verify token is still valid
      authAPI
        .getMe()
        .then((userData) => {
          setUser(userData);
          sessionStorage.setItem("user", JSON.stringify(userData));
        })
        .catch(() => {
          logout();
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const userData = await authAPI.login({ email, password });
    const { access_token, user } = userData;
    sessionStorage.setItem("token", access_token);
    sessionStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    // Refetch /me so we always have the canonical user (including role) from the server
    try {
      const me = await authAPI.getMe();
      setUser(me);
      sessionStorage.setItem("user", JSON.stringify(me));
      return me;
    } catch {
      return user;
    }
  };

  const register = async (name, email, password) => {
    const userData = await authAPI.register({ name, email, password });
    const { access_token, user } = userData;
    sessionStorage.setItem("token", access_token);
    sessionStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    return user;
  };

  const logout = useCallback(() => {
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const userData = await authAPI.getMe();
      setUser(userData);
      sessionStorage.setItem("user", JSON.stringify(userData));
      return true;
    } catch {
      logout();
      return false;
    }
  }, [logout]);

  // Track whether the expiry warning has already been shown this session
  const expiryWarnedRef = useRef(false);

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token || !user) return;

    const checkExpiry = () => {
      const expiry = getTokenExpiry(token);
      if (!expiry) return;
      const now = Date.now();
      const timeLeft = expiry - now;

      if (timeLeft <= 0) {
        toast.error("Your session has expired. Please log in again.");
        logout();
      } else if (timeLeft <= 30 * 60 * 1000 && !expiryWarnedRef.current) {
        expiryWarnedRef.current = true;
        const minutes = Math.round(timeLeft / 60000);
        toast.warning(`Your session expires in ${minutes} minute${minutes !== 1 ? 's' : ''}. Save your work.`);
      }
    };

    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
  }, [user, logout]);

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    refreshSession,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    isClient: user?.role === "client",
    clientId: user?.client_id || null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
